/**
 * Agent identity. Agents get an index from 1 in join order. One helper owns colour, head and callsign so badges
 * match everywhere: the map, the roster, the timeline and threads.
 *
 * Indexes 1 to 12 take agent-01 to agent-12 with the default head from the token usage notes. Beyond 12 the
 * colour cycles and the head steps one place each lap (scout, relay, sentinel), so index 13 is agent-01 with a
 * relay head and is still told apart from index 1 by head shape and callsign.
 */

export const AGENT_HEADS = ["scout", "relay", "sentinel"] as const;
export type AgentHead = (typeof AGENT_HEADS)[number];

export const AGENT_COLOURS = 12;

/** Default head per colour slot, from the token usage notes (agent-01 scout, agent-02 relay, ...). */
const DEFAULT_HEAD: readonly AgentHead[] = [
  "scout",
  "relay",
  "sentinel",
  "scout",
  "relay",
  "sentinel",
  "relay",
  "sentinel",
  "scout",
  "sentinel",
  "scout",
  "relay",
];

export interface AgentIdentity {
  /** 1-based join order. */
  readonly index: number;
  /** Colour slot 1 to 12. */
  readonly slot: number;
  /** Two-digit slot, "03". */
  readonly slotName: string;
  readonly head: AgentHead;
  /** AX-03. Mono. Index over 99 keeps its digits. */
  readonly callsign: string;
  readonly badgeClass: string;
  readonly headClass: string;
  /** The CSS variable holding the agent's colour. */
  readonly colourVar: string;
}

const pad = (n: number): string => String(n).padStart(2, "0");

export function agentIdentity(index: number): AgentIdentity {
  if (!Number.isInteger(index) || index < 1) throw new RangeError(`agent index must be a whole number from 1, got ${index}`);
  const slot = ((index - 1) % AGENT_COLOURS) + 1;
  const lap = Math.floor((index - 1) / AGENT_COLOURS);
  const base = DEFAULT_HEAD[slot - 1] ?? "scout";
  const head = AGENT_HEADS[(AGENT_HEADS.indexOf(base) + lap) % AGENT_HEADS.length] ?? base;
  return {
    index,
    slot,
    slotName: pad(slot),
    head,
    callsign: `AX-${pad(index)}`,
    badgeClass: `bt-agent--${pad(slot)}`,
    headClass: `bt-agent--${head}`,
    colourVar: `--agent-${pad(slot)}`,
  };
}

/** Parse "AX-07" back to an identity, or null. */
export function identityFromCallsign(callsign: string): AgentIdentity | null {
  const match = /^AX-(\d{2,})$/.exec(callsign);
  const index = match?.[1] ? Number.parseInt(match[1], 10) : 0;
  return index >= 1 ? agentIdentity(index) : null;
}

/** Assigns indexes in join order and remembers them, so an agent keeps its identity for the whole run. */
export class AgentRoster {
  private readonly order: string[] = [];

  /** The identity for an agent id, joining it if new. */
  join(agentId: string): AgentIdentity {
    let at = this.order.indexOf(agentId);
    if (at === -1) at = this.order.push(agentId) - 1;
    return agentIdentity(at + 1);
  }

  get(agentId: string): AgentIdentity | null {
    const at = this.order.indexOf(agentId);
    return at === -1 ? null : agentIdentity(at + 1);
  }

  get size(): number {
    return this.order.length;
  }
}
